import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import type { AxiosResponse } from 'axios';
import { useNotification } from 'hooks/useNotification';
import { integrationQueryKeys } from './integrationQueryKeys';
import { CreateDraftParams, CreateDraftResponse } from 'IntegrationModule/types';
import { createDraftIntegrationApi } from '../createDraftIntegrationApi';

export const useCreateDraftMutation = () => {
  const token = useSelector(selectAuthToken);
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    AxiosResponse<CreateDraftResponse>,
    AxiosResponse<{ error: string; message: string; statusCode: number }>,
    CreateDraftParams
  >({
    mutationFn: async (params) => {
      if (!token) throw new Error('Not Authenticated');
      return createDraftIntegrationApi({ params, token }) as Promise<
        AxiosResponse<CreateDraftResponse>
      >;
    },
    onSuccess: () => {
      showSuccess('Draft integration created');
      queryClient.invalidateQueries({ queryKey: integrationQueryKeys.lists() });
    },
    onError: (error) => {
      showError(`Failed to create draft integration: ${error?.data?.message || 'Unknown error'}`);
    },
  });

  return {
    createDraft: mutation.mutate,
    createDraftAsync: mutation.mutateAsync,
    isCreatingDraft: mutation.isPending,
  };
};
